import { Stack, Box, Typography } from "@mui/material";
import moment from "moment";
import { QRCodeSVG } from "qrcode.react";
import StatusChip from "src/shared/components/status-chip";
import { ICarRequest } from "src/types/car-request";
import sarIcon from "src/assets/svg-icons/sar.svg";
import { useTranslation } from "react-i18next";

const TicketInfoBox = ({
  data,
  hiddenPaymentStatus = false,
}: {
  data: ICarRequest;
  hiddenPaymentStatus?: boolean;
}) => {
  const {
    ticketNumber,
    plateNumber,
    checkInTime,
    enableFreeLocation,
    paymentInfo,
    price,
    plateLetter,
    hasGlobalDiscount,
    globalDiscountPer,
    publicId,
    pinCode,
  } = data || {};
  const isFreeTicket = enableFreeLocation || price === 0;
  const { t, i18n } = useTranslation();

  const paymentStatus =
    !isFreeTicket && !!paymentInfo && paymentInfo.unpaidAmount <= 0
      ? "Paid"
      : !isFreeTicket &&
          !!paymentInfo &&
          paymentInfo.amount > 0 &&
          paymentInfo.unpaidAmount > 0
        ? "Partially Paid"
        : "Unpaid";

  const priceAfterDiscount =
    (paymentInfo.amount ?? 0) + (paymentInfo.unpaidAmount ?? 0);

  return (
    <>
      <Stack alignItems={"center"} mb={4.5}>
        <Box bgcolor={"white"} borderRadius={"6px"} pt={1.5} pb={1} px={1}>
          <QRCodeSVG value={publicId} />
        </Box>
      </Stack>

      <Stack gap={1.5}>
        {pinCode ? (
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography
              color={"textPrimary"}
              fontWeight={700}
              style={{ direction: "ltr" }}
            >
              {t("pinCode")}
            </Typography>
            <Stack
              direction={i18n.language === "en" ? "row" : "row-reverse"}
              gap={0.7}
            >
              {pinCode.split("").map((char: any, index: number) => (
                <Box
                  key={index}
                  padding={"1px 10px"}
                  borderRadius={"8px"}
                  bgcolor={"#D4AD53"}
                >
                  <Typography fontWeight={700}>{char}</Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        ) : null}

        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography color={"textPrimary"} fontSize={"14px"}>
            {t("ticketNum")}
          </Typography>
          <Typography
            color={"textPrimary"}
            fontWeight={700}
            style={{ direction: "ltr" }}
          >
            {ticketNumber}
          </Typography>
        </Stack>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography color={"textPrimary"} fontSize={"14px"}>
            {t("vehicleNum")}
          </Typography>
          <Typography color={"textPrimary"} fontWeight={700}>
            {!!plateLetter || !!plateNumber
              ? `${plateLetter ?? ""}${plateNumber ?? ""}`
              : "--"}
          </Typography>
        </Stack>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography color={"textPrimary"} fontSize={"14px"}>
            {t("date")}
          </Typography>
          <Typography color={"textPrimary"} fontWeight={700}>
            {checkInTime ? moment(checkInTime).format("DD/MM/YYYY") : "--"}
          </Typography>
        </Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography color={"textPrimary"} fontSize={"14px"}>
            {t("fees")}
          </Typography>
          {isFreeTicket ? (
            <StatusChip label="Free" />
          ) : (
            <Stack direction={"row"} alignItems={"center"} gap={"4px"}>
              <Stack
                direction={i18n.language === "en" ? "row" : "row-reverse"}
                alignItems={"center"}
                gap={"4px"}
              >
                <img height={14} src={sarIcon} alt="" />
                <Typography color={"textPrimary"} fontWeight={700}>
                  {priceAfterDiscount}
                </Typography>
              </Stack>

              {/* {hasGlobalDiscount && (
                <Typography
                  color={"#4EBE86"}
                  fontSize={"14px"}
                  component={"span"}
                >
                  ( %{globalDiscountPer} {t("discountApplied")} )
                </Typography>
              )} */}
            </Stack>
          )}
        </Stack>

        {!isFreeTicket &&
          !hiddenPaymentStatus &&
          paymentStatus !== "Partially Paid" && (
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography color={"textPrimary"} fontSize={"14px"}>
                {t("paymentStatus")}
              </Typography>
              <StatusChip
                label={paymentStatus === "Paid" ? t("paid") : t("unpaid")}
                variant={paymentStatus === "Paid" ? "success" : "error"}
              />
            </Stack>
          )}
        {paymentStatus === "Partially Paid" && (
          <Box mt={1.5} textAlign={"center"}>
            <Typography color={"#868686"} fontSize={"14px"} fontWeight={500}>
              {t("totalPaidFees")}
            </Typography>
            <Stack
              my={1}
              direction={"row"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={"4px"}
            >
              <Stack
                direction={i18n.language === "en" ? "row" : "row-reverse"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"4px"}
              >
                <img height={19} src={sarIcon} alt="" />
                <Typography
                  color={"#4EBE86"}
                  fontSize={"25px"}
                  fontWeight={700}
                >
                  {paymentInfo.amount}
                </Typography>
              </Stack>
              {/* {hasGlobalDiscount && (
                <Typography
                  color={"#4EBE86"}
                  fontSize={"14px"}
                  component={"span"}
                >
                  ( %{globalDiscountPer} {t("discountApplied")} )
                </Typography>
              )} */}
            </Stack>
            <Typography color={"#868686"} fontSize={"14px"} fontWeight={500}>
              {t("extraFeesApplied")}
            </Typography>
            <Stack
              my={1}
              direction={"row"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={"4px"}
            >
              <Stack
                direction={i18n.language === "en" ? "row" : "row-reverse"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"4px"}
              >
                <img height={19} src={sarIcon} alt="" />
                <Typography
                  color={"#FF4A4A"}
                  fontSize={"25px"}
                  fontWeight={700}
                >
                  {paymentInfo.unpaidAmount}{" "}
                </Typography>
              </Stack>

              {/* {hasGlobalDiscount && (
                <Typography
                  color={"#4EBE86"}
                  fontSize={"14px"}
                  component={"span"}
                >
                  ( %{globalDiscountPer} {t("discountApplied")} )
                </Typography>
              )} */}
            </Stack>
          </Box>
        )}
      </Stack>
    </>
  );
};

export { TicketInfoBox };
